import { useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { AccountData, DisputeItem } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import { getUserData, getEventData } from "../../utils/FirebaseUtils";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { testStripe } from "../../utils/stripeAccountUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { API } from "../../API";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import Skeleton from "react-loading-skeleton";
import TextHoverInlineButton from "../Buttons/TextHoverInlineButton";
import { GetDateWithTime, MARKIT_DOCS } from "@markit/common.utils";
import HelpModal from "../Containers/HelpModal";
import ProfileItem from "../ProfileItem";
import FullProfilePreviewModal from "../FollowerProfile/FullProfilePreviewModal";

type DisputeRow = DisputeItem & {
  userData: AccountData | undefined;
  eventTitle: string;
};

const TABLE_COLS: { label: string; flex: number }[] = [
  { label: "Customer", flex: 1.5 },
  { label: "Affiliated Charge", flex: 2 },
  { label: "Filed Dispute", flex: 1.5 },
  { label: "Evidence Due By", flex: 1.2 },
  { label: "Amount", flex: 0.8 },
];

const DisputesPanel = () => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const [rows, setRows] = useState<DisputeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [helpModal, setHelpModal] = useState(false);
  const [profilePreviewSelected, setProfilePreviewSelected] = useState<
    AccountData | undefined
  >(undefined);

  useAsyncOnMount(async () => {
    const { userDisputes } = await (
      testStripe(accountData.uid)
        ? API.testPayment.listDisputes
        : API.payment.listDisputes
    )({ uid: accountData.uid });

    const resolved = await Promise.all(
      userDisputes.map(async (d) => {
        const [userData, eventData] = await Promise.all([
          getUserData(d.disputeUid),
          getEventData(d.eventId),
        ]);
        return {
          ...d,
          userData: userData,
          eventTitle: eventData?.title ?? "Unknown Event",
        };
      })
    );
    setRows(resolved);
    setLoading(false);
  });

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 20 }}>
        <StandardBorderedContainer
          containerStyles={{ display: "block", backgroundColor: Colors.WHITE }}
        >
          <div className="ColumnNormal" style={{ paddingBlock: 14 }}>
            <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
              <span className="sectionTitle">Disputes Overview</span>
              <span className="smallBodySubtext">
                When a customer disputes a charge, the disputed amount is
                automatically reversed from your Stripe account. A $15 dispute
                fee is also charged to your Stripe balance to cover processing
                costs.{" "}
                <TextHoverInlineButton
                  text="Learn More"
                  onPress={() =>
                    window.open(MARKIT_DOCS + "/disputes", "_blank")
                  }
                  textStyles={{ color: Colors.BLUE5, fontWeight: 500 }}
                />
              </span>
            </div>
            <HorizontalDivider altMargin={14} />
            <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
              <span className="sectionTitle">Challenging Disputes</span>
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="smallBodySubtext">
                  First, we recommend reaching out to the purchasers as soon as
                  possible to try reaching an agreement to withdraw their
                  dispute.
                </span>
                <span className="smallBodySubtext">
                  If that fails and you&apos;d like to challenge the dispute
                  directly, contact our support team{" "}
                  <TextHoverInlineButton
                    text="here"
                    onPress={() => setHelpModal(true)}
                    textStyles={{ color: Colors.BLUE5, fontWeight: 500 }}
                  />
                  . If the dispute is resolved in your favor, the reversed
                  payment will be restored to your account.
                </span>
              </div>
            </div>
          </div>
        </StandardBorderedContainer>

        <div className="ColumnNormal" style={{ gap: 10 }}>
          {loading ? (
            <Skeleton height={16} width={120} />
          ) : (
            <span className="bodyMedium">Total Disputes ({rows.length})</span>
          )}
          <StandardBorderedContainer
            containerStyles={{
              display: "block",
              overflow: "hidden",
              backgroundColor: Colors.WHITE,
            }}
          >
            <div className="AlignedRow" style={{ padding: 14, gap: 14 }}>
              {TABLE_COLS.map((col) => (
                <span
                  key={col.label}
                  className="bodySubtext"
                  style={{ flex: col.flex, fontWeight: 500 }}
                >
                  {col.label}
                </span>
              ))}
            </div>
            <HorizontalDivider />
            {loading ? (
              [0, 1, 2].map((i) => (
                <div key={i}>
                  <div
                    className="AlignedRow"
                    style={{ paddingBlock: 18, paddingInline: 14, gap: 16 }}
                  >
                    {TABLE_COLS.map((col) => (
                      <div key={col.label} style={{ flex: col.flex }}>
                        <Skeleton height={24} />
                      </div>
                    ))}
                  </div>
                  {i < 2 && <HorizontalDivider />}
                </div>
              ))
            ) : rows.length === 0 ? (
              <div className="Centering" style={{ paddingBlock: 40 }}>
                <span className="smallBodySubtext">No disputes to show</span>
              </div>
            ) : (
              rows.map((row, i) => (
                <div key={row.id}>
                  <div
                    className="AlignedRow"
                    style={{
                      paddingBlock: 18,
                      paddingInline: 14,
                      gap: 16,
                      backgroundColor: Colors.WHITE,
                    }}
                  >
                    <span
                      className="bodyMedium"
                      style={{ flex: TABLE_COLS[0].flex }}
                    >
                      <ProfileItem
                        profileItem={row.userData}
                        onItemPress={() =>
                          setProfilePreviewSelected(row.userData)
                        }
                      />
                    </span>
                    <span
                      className="bodySubtext"
                      style={{ flex: TABLE_COLS[1].flex }}
                    >
                      {row.eventTitle}
                    </span>
                    <span
                      className="bodySubtext"
                      style={{ flex: TABLE_COLS[2].flex }}
                    >
                      {GetDateWithTime(row.createdAt, true)}
                    </span>
                    <span
                      className="bodySubtext"
                      style={{ flex: TABLE_COLS[3].flex }}
                    >
                      {GetDateWithTime(row.dueAt, true)}
                    </span>
                    <span
                      className="bodyMedium"
                      style={{ flex: TABLE_COLS[4].flex, color: Colors.RED3 }}
                    >
                      -{FormatCurrency(row.amount / 100)}
                    </span>
                  </div>
                  {i < rows.length - 1 && <HorizontalDivider />}
                </div>
              ))
            )}
          </StandardBorderedContainer>
        </div>
      </div>
      {helpModal ? (
        <HelpModal showModal={helpModal} setShowModal={setHelpModal} />
      ) : null}
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
        />
      ) : null}
    </>
  );
};

export default DisputesPanel;
